/**
 * IndexedDB wrapper for persisting warband member images as binary Blobs.
 *
 * Database : frostgrave-images  (version 1)
 * Store    : member-images
 * Key      : "{warbandId}:{memberId}"
 * Value    : Blob (the raw image file, typically PNG/JPEG)
 *
 * Each exported function opens the database, performs its operation, then
 * closes the connection.  This keeps the connection footprint minimal and
 * avoids stale-connection issues during hot-module reloads in development.
 */

const DB_NAME = 'frostgrave-images';
const STORE_NAME = 'member-images';
const DB_VERSION = 1;

function imageKey(warbandId: string, memberId: string): string {
  return `${warbandId}:${memberId}`;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export const imageDbService = {
  /** Persist an image Blob for the given warband member. */
  saveImage: async (warbandId: string, memberId: string, blob: Blob): Promise<void> => {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(blob, imageKey(warbandId, memberId));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  },

  /** Load the image Blob for the given warband member, or null if absent. */
  loadImage: async (warbandId: string, memberId: string): Promise<Blob | null> => {
    const db = await openDb();
    const result = await new Promise<Blob | null>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(imageKey(warbandId, memberId));
      req.onsuccess = () => resolve((req.result as Blob | undefined) ?? null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return result;
  },

  /** Remove the image for the given warband member. */
  deleteImage: async (warbandId: string, memberId: string): Promise<void> => {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(imageKey(warbandId, memberId));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  },
};
